import * as z from "zod";
import {
  SupplyIntelUnitTotalsSchema,
  totalSupplyIntelUnitRows,
} from "./brand-list";
import type { SupplyIntelBrandUnitRow } from "./brand-units";
import type { SupplyIntelConfig } from "./config";
import { SupplyIntelGradeStatus, SupplyIntelNextStep } from "./next-step";

/** How many products the brand pitch names. */
const SUPPLY_INTEL_TOP_PRODUCT_COUNT = 3;

const SupplyIntelBrandProductRowSchema_ = z.object({
  /** The product family ID, or the product's when it has no family. */
  id: z.string(),
  title: z.string().nullable(),
  /** Each variant's SKU, sorted. */
  skus: z.array(z.string()),
  variants: z.number(),
  /** The category's full path; null without one. */
  category: z.string().nullable(),
  /** Sold as new and past the refulfillment timer, worth reselling. */
  likeNewResaleUnits: z.number(),
  /** Sold as new and past the refulfillment timer, for an off-price lot or liquidation. */
  likeNewLotUnits: z.number(),
  /** Grades the brand doesn't sell as new, worth reselling. */
  preOwnedResaleUnits: z.number(),
  /** Grades the brand doesn't sell as new, for an off-price lot or liquidation. */
  preOwnedLotUnits: z.number(),
  /** In process, held, needing review, excluded or already routed. */
  otherUnits: z.number(),
  totals: SupplyIntelUnitTotalsSchema,
});
export const SupplyIntelBrandProductRowSchema: z.ZodType<SupplyIntelBrandProductRow> =
  SupplyIntelBrandProductRowSchema_;
export interface SupplyIntelBrandProductRow extends z.infer<
  typeof SupplyIntelBrandProductRowSchema_
> {}

const SupplyIntelTopProductSchema_ = z.object({
  title: z.string().nullable(),
  /** Units that leave the brand's store: resale, an off-price lot or liquidation. */
  units: z.number(),
  /** Those units at store price. */
  valueUsd: z.number(),
});
export const SupplyIntelTopProductSchema: z.ZodType<SupplyIntelTopProduct> =
  SupplyIntelTopProductSchema_;
export interface SupplyIntelTopProduct extends z.infer<
  typeof SupplyIntelTopProductSchema_
> {}

function productKey(row: SupplyIntelBrandUnitRow): string {
  return row.productFamilyId ?? row.productId ?? row.sku ?? "";
}

/** One row per store product, from its variants' unit rows, in no particular order. */
export function rollUpSupplyIntelProducts(
  rows: readonly SupplyIntelBrandUnitRow[],
  config: SupplyIntelConfig,
): SupplyIntelBrandProductRow[] {
  const byProduct = new Map<string, SupplyIntelBrandUnitRow[]>();
  for (const row of rows) {
    const key = productKey(row);
    const productRows = byProduct.get(key);
    if (productRows) {
      productRows.push(row);
    } else {
      byProduct.set(key, [row]);
    }
  }
  return [...byProduct].map(([id, productRows]) => {
    const totals = totalSupplyIntelUnitRows(productRows, config);
    const units = (
      step: SupplyIntelNextStep,
      gradeStatus?: SupplyIntelGradeStatus,
    ) =>
      productRows
        .filter(
          (row) =>
            row.nextStep === step &&
            (gradeStatus === undefined || row.gradeStatus === gradeStatus),
        )
        .reduce((sum, row) => sum + row.units, 0);
    const lotUnits = (gradeStatus: SupplyIntelGradeStatus) =>
      units(SupplyIntelNextStep.OffPriceLot, gradeStatus) +
      units(SupplyIntelNextStep.Liquidate, gradeStatus);
    const likeNewResaleUnits = units(SupplyIntelNextStep.Resale);
    const likeNewLotUnits = lotUnits(SupplyIntelGradeStatus.SellAsNew);
    const preOwnedResaleUnits = units(SupplyIntelNextStep.ResalePreOwned);
    const preOwnedLotUnits = lotUnits(SupplyIntelGradeStatus.NotSoldAsNew);
    const first = productRows[0];
    return {
      id: id || "none",
      title: first?.productFamilyTitle ?? first?.title ?? first?.sku ?? null,
      skus: [
        ...new Set(productRows.flatMap((row) => (row.sku ? [row.sku] : []))),
      ].sort(),
      variants: new Set(productRows.map((row) => row.productId)).size,
      category: productRows.find((row) => row.category)?.category ?? null,
      likeNewResaleUnits,
      likeNewLotUnits,
      preOwnedResaleUnits,
      preOwnedLotUnits,
      otherUnits:
        totals.units -
        totals.keepOnBrandStoreUnits -
        likeNewResaleUnits -
        likeNewLotUnits -
        preOwnedResaleUnits -
        preOwnedLotUnits,
      totals,
    };
  });
}

/** The products with the most store value leaving the brand's store, for the brand pitch. */
export function supplyIntelTopProducts(
  products: readonly SupplyIntelBrandProductRow[],
): SupplyIntelTopProduct[] {
  return products
    .filter((product) => product.totals.actionableValueUsd > 0)
    .sort(
      (a, b) =>
        b.totals.actionableValueUsd - a.totals.actionableValueUsd ||
        (a.title ?? "").localeCompare(b.title ?? ""),
    )
    .slice(0, SUPPLY_INTEL_TOP_PRODUCT_COUNT)
    .map((product) => ({
      title: product.title,
      units: product.totals.resaleUnits + product.totals.lotOrLiquidateUnits,
      valueUsd: product.totals.actionableValueUsd,
    }));
}
